import PropTypes from 'prop-types';
import React from 'react';
import {
  OlsConfigStore,
  RouteHandler,
  ScrollWidgetActions,
  ShopViewConstants
} from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import dataAids from '../../constants/dataAids';
import { hasSalePrice, calculateSalePercentage, formatPrice } from './utils/ProductUtils';
import ShopLink from './ShopLink';
import { mapContextToProps } from './BaseContainerContext';

export class ProductGroupItem extends React.Component {
  constructor() {
    super(...arguments);
    this.handleOnClick = this.handleOnClick.bind(this);
  }

  salePercentageText(product) {
    const { renderPrices, translate } = this.props;
    if (!renderPrices || !hasSalePrice(product)) return null;

    const { price, sale_price } = product;
    const percentage = calculateSalePercentage(price, sale_price);
    if (percentage < 1) return null;

    return translate('products.sale_label');
  }

  handleOnClick(e) {
    e.preventDefault();

    const { product } = this.props;
    RouteHandler.navigate(ShopViewConstants.PRODUCT_DETAIL, { slug: product.slug });
    ScrollWidgetActions.scrollShopWidget();
  }

  determinePrice(price) {
    return !['object', 'undefined'].includes(typeof price)
      ? formatPrice({ currency: true, number: +price })
      : price;
  }

  get eyebrowText() {
    const { product, translate } = this.props;

    if (product.limited_edition) {
      return translate('products.limited_edition');
    } else if (product.ship_free) {
      return translate('products.free_shipping');
    }
    return null;
  }

  /* eslint-disable complexity */
  get classicProductData() {
    const {
      product,
      showOnlyProductName,
      isForegroundImage,
      imageDimensions,
      cardSize,
      imageCropMethod,
      imageShape,
      hasImageBorder,
      translate
    } = this.props;
    const { name, id, product_type, default_asset_url, has_price_range } = product;
    const saleRibbon = this.salePercentageText(product, translate);
    const price = this.determinePrice(product.price);
    const salePrice = hasSalePrice(product) ? this.determinePrice(product.sale_price) : null;
    const imagePath = default_asset_url || OlsConfigStore.getState().noProductImage;
    return {
      id,
      name,
      productType: product_type,
      isPriceShown: !showOnlyProductName,
      price: (price && price.display) || price,
      salePrice: (salePrice && salePrice.display) || salePrice,
      imageUrl: !isForegroundImage && imagePath,
      imageData: isForegroundImage &&
        product.has_images && {
        image: imagePath,
        ...imageDimensions
      },
      cardSize,
      imageSrc: !product.has_images && imagePath,
      ribbonText: saleRibbon,
      imageCropMethod,
      eyebrowText: !showOnlyProductName && this.eyebrowText,
      showOnlyProductName: showOnlyProductName,
      footerText: null,
      imageShape,
      hasImageBorder,
      priceText: has_price_range ? translate('products.from') : null
    };
  }

  get gridProductData() {
    return {
      ...this.classicProductData,
      hasImageBorder: false
    };
  }

  render() {
    const {
      product,
      shopWidgetSamePage,
      shopWidgetPageId,
      shopWidgetRoute,
      productItemAs: ProductItemComponent,
      renderMode,
      isGridLayout,
      ...props
    } = this.props;

    const { name } = product;

    const pageQuery = `olsPage=products/${product.slug}`;
    const pagePath = `ols/products/${product.slug}`;

    const productProps = isGridLayout ? this.gridProductData : this.classicProductData;

    const productDataAids = {
      name: `${dataAids.PRODUCT_GROUP_CARD_NAME_RENDERED}_${name}`,
      price: `${dataAids.PRODUCT_GROUP_PRICE_RENDERED}_${name}`,
      image: `${dataAids.PRODUCT_GROUP_IMAGE_RENDERED}_${name}`,
      salePrice: `${dataAids.PRODUCT_GROUP_SALE_PRICE_RENDERED}_${name}`,
      eyebrow: `${dataAids.PRODUCT_GROUP_EYEBROW_RENDERED}_${name}`,
      footer: `${dataAids.PRODUCT_GROUP_FOOTER_RENDERED}_${name}`,
      ribbon: `${dataAids.PRODUCT_GROUP_RIBBON_RENDERED}_${name}`
    };

    const productContent = (
      <ProductItemComponent dataAids={ productDataAids } { ...productProps } { ...props } />
    );

    if (shopWidgetPageId) {
      const baseProps = {
        'href': `${shopWidgetRoute}/${pagePath}`,
        'onClick': this.handleOnClick,
        'data-page': shopWidgetPageId,
        'data-page-query': pageQuery,
        'data-aid': `${dataAids.PRODUCT_GROUP_NAME_RENDERED}_${name}`
      };

      return (
        <ShopLink
          baseProps={ baseProps }
          shopWidgetSamePage={ shopWidgetSamePage }
          renderMode={ renderMode }
          children={ productContent }
        />
      );
    }

    return productContent;
  }
}

ProductGroupItem.propTypes = {
  product: PropTypes.object.isRequired,
  renderPrices: PropTypes.bool,
  shopWidgetSamePage: PropTypes.any,
  shopWidgetPageId: PropTypes.any,
  shopWidgetRoute: PropTypes.any,
  renderMode: PropTypes.string,
  productItemAs: PropTypes.elementType.isRequired,
  isGridLayout: PropTypes.bool,
  showOnlyProductName: PropTypes.bool,
  isForegroundImage: PropTypes.bool,
  imageDimensions: PropTypes.object,
  cardSize: PropTypes.object,
  imageCropMethod: PropTypes.string,
  imageShape: PropTypes.string,
  hasImageBorder: PropTypes.bool,
  translate: PropTypes.func
};

ProductGroupItem.defaultProps = {
  imageShape: 'square',
  hasImageBorder: false,
  translate: () => {}
};

export default mapContextToProps(ProductGroupItem, context => ({
  translate: context.translate
}));
